import { datadogLogs, HandlerType, StatusType } from '@datadog/browser-logs';
import { getTokenFromUrl } from '../doc-editor';
import { redactTokenFromPayload, TokenInfo } from '../token';
import { window } from '../utils/browser';
import { ConsoleIndex, LoggerType, LogMethods } from './types';

// SSR_TODO: Handle logging when server-side rendered.
// 1. Should logging even happen on the server?
// 2. Does that work with DataDog's stuff?
// 3. What about ErrorBoundary on the server?
let __logger__: LoggerType | null = null;
let __tokenInfo__: TokenInfo | undefined = undefined;
let __token_error__: any = null;

const __host__ = window?.location.host ?? '';
const __url__ = window?.location.href ?? '';
const __service__ = window?.location.pathname ?? '';

function init() {
  if (!__logger__) {
    // --- Setup DataDog Logger ---
    const isDeployed = import.meta.env.PROD;
    const clientToken = isDeployed
      ? 'pub0f349884923eab9c548f832f40dcbbd4'
      : 'pub663d9df9179a416aa7ac4637a8b50376';

    datadogLogs.init({
      clientToken,
      site: 'datadoghq.com',
      sessionSampleRate: 100,
      usePartitionedCrossSiteSessionCookie: true,
      // Unless we override the default log (and maybe more) for datadog this will log the user-token in the url
      forwardErrorsToLogs: false,
    });

    datadogLogs.setGlobalContext({
      env: isDeployed ? 'prod' : 'nonprod',
    });
    __logger__ = datadogLogs.createLogger('zDocsLogger', {
      level: isDeployed ? StatusType.warn : StatusType.debug,
      handler: isDeployed ? HandlerType.http : HandlerType.silent,
    }) as LoggerType;
  }

  if (!__tokenInfo__) {
    try {
      // --- Parse token for more helpful log context ---
      __tokenInfo__ = getTokenFromUrl();
    } catch (error) {
      // Bad things, but make sure we always have some information provided.
      __token_error__ = error;

      const token = '';
      const payload = {
        zdocs: {
          tenantId: 'no_payload_from_token',
        },
      } as any;

      __tokenInfo__ = {
        token,
        redactedToken: token,
        payload,
        redactedPayload: payload,
      };
    }
  }
}

const _logMessage = (
  message: string,
  messageContext: any,
  methodName: LogMethods
) => {
  try {
    init();

    messageContext = redactTokenFromPayload(
      {
        ...messageContext,
        host: __host__,
        service: __service__,
        view: { url: __url__ },
        token: __tokenInfo__?.redactedPayload,
        tokenError: __token_error__,
      },
      __tokenInfo__
    );

    // Log to data-dog if possible
    if (__logger__ && __logger__[methodName]) {
      __logger__[methodName](message, messageContext);
    }

    // Always log to console
    const tmpConsole = console as ConsoleIndex;
    if (import.meta.env.MODE !== 'test' && tmpConsole[methodName]) {
      tmpConsole[methodName]({ message, messageContext });
    }
  } catch (error) {
    console.error('Failed to log message', error);
  }
};

const logger: LoggerType = {
  debug: function (message: string, messageContext?: any) {
    _logMessage(message, messageContext, 'debug');
  },
  info: function (message: string, messageContext?: any) {
    _logMessage(message, messageContext, 'info');
  },
  warn: function (message: string, messageContext?: any) {
    _logMessage(message, messageContext, 'warn');
  },
  error: function (message: string, messageContext?: any) {
    _logMessage(message, messageContext, 'error');
  },
};

export default logger;
